// src/lib/kinema/konva/overlays/SelectionOverlay.ts
import Konva from 'konva';

export interface AnchorDragCallback {
  onAnchorDragEnd: (layerId: string, newAnchorX: number, newAnchorY: number) => void;
}

export class SelectionOverlay {
  private transformer: Konva.Transformer;
  private anchorMarker: Konva.Group;
  private callbacks: AnchorDragCallback | null = null;
  private currentLayerId: string | null = null;

  constructor(layer: Konva.Layer) {
    this.transformer = new Konva.Transformer({
      rotateEnabled: true,
      borderStroke: '#2563eb',
      anchorStroke: '#2563eb',
      anchorFill: '#ffffff',
      anchorSize: 8,
      keepRatio: false,
      shouldOverdrawWholeArea: true,
    });
    layer.add(this.transformer);

    // Anchor point marker — crosshair with circle
    this.anchorMarker = new Konva.Group({ visible: false, draggable: true });

    // Outer circle
    const circle = new Konva.Circle({
      radius: 6,
      fill: 'rgba(37, 99, 235, 0.15)',
      stroke: '#2563eb',
      strokeWidth: 1.5,
    });
    this.anchorMarker.add(circle);

    // Crosshair
    const hLine = new Konva.Line({ points: [-8, 0, 8, 0], stroke: '#2563eb', strokeWidth: 1 });
    const vLine = new Konva.Line({ points: [0, -8, 0, 8], stroke: '#2563eb', strokeWidth: 1 });
    this.anchorMarker.add(hLine);
    this.anchorMarker.add(vLine);

    // Center dot
    const dot = new Konva.Circle({ radius: 2, fill: '#2563eb' });
    this.anchorMarker.add(dot);

    this.anchorMarker.on('dragend', () => {
      if (!this.callbacks || !this.currentLayerId) return;

      // The anchor marker is positioned in canvas space at the anchor's canvas position.
      // We need to convert the new position back to layer-local anchor coordinates.
      const nodes = this.transformer.nodes();
      if (nodes.length !== 1) return;
      const node = nodes[0];

      // New canvas position of the anchor
      const newCanvasX = this.anchorMarker.x();
      const newCanvasY = this.anchorMarker.y();

      // The node's position (x, y) should move to the new anchor canvas position
      // and the anchorX/anchorY should be recalculated so the visual doesn't shift.
      // anchorX/anchorY are in unscaled local space.
      // topLeft = canvasX - anchorX (when scale=1, rotation=0)
      // For scaled/rotated nodes this is more complex, but for the common case:
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const oldAnchorX = node.offsetX();
      const oldAnchorY = node.offsetY();
      const oldX = node.x();
      const oldY = node.y();

      // The visual top-left stays the same. New anchor in local coords:
      // newAnchorX = oldAnchorX + (newCanvasX - oldX) / scaleX
      const newAnchorX = oldAnchorX + (newCanvasX - oldX) / scaleX;
      const newAnchorY = oldAnchorY + (newCanvasY - oldY) / scaleY;

      this.callbacks.onAnchorDragEnd(this.currentLayerId, newAnchorX, newAnchorY);
    });

    layer.add(this.anchorMarker);
  }

  setCallbacks(callbacks: AnchorDragCallback): void {
    this.callbacks = callbacks;
  }

  updateSelection(selectedIds: Set<string>, nodeMap: Map<string, Konva.Node>): void {
    const nodes = Array.from(selectedIds)
      .map(id => nodeMap.get(id))
      .filter(Boolean) as Konva.Node[];
    this.transformer.nodes(nodes);

    // Show anchor marker for single selection
    if (nodes.length === 1) {
      const node = nodes[0];
      const layerId = node.getAttr('layerId') as string;
      this.currentLayerId = layerId;

      // Position the anchor marker at the anchor's canvas position
      // node.x() is where the anchor is on canvas
      this.anchorMarker.x(node.x());
      this.anchorMarker.y(node.y());
      this.anchorMarker.visible(true);
    } else {
      this.anchorMarker.visible(false);
      this.currentLayerId = null;
    }
  }

  /** Call on every frame during transforms to keep anchor marker synced */
  refreshAnchorPosition(): void {
    const nodes = this.transformer.nodes();
    if (nodes.length === 1 && this.anchorMarker.visible()) {
      this.anchorMarker.x(nodes[0].x());
      this.anchorMarker.y(nodes[0].y());
    }
  }

  /** Enable/disable anchor dragging (disable when pan tool is active) */
  setAnchorDraggable(draggable: boolean): void {
    this.anchorMarker.draggable(draggable);
  }

  getTransformer(): Konva.Transformer {
    return this.transformer;
  }
}
